import React from "react";
import { i18nManager } from "../../../../i18n";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { BaseService, IHostBlogArticle, IHostManager, IHostWebsite, IWebsiteEvent, ITournament, OmneHostService, SanitizedResponse } from "../../../../services";
import { buildEventView } from "./Events";
import { Utils } from "../../../../utils";
import { Classname } from "@rivtn/noseur";
import { buildArticleView } from "./Blog";
import { useNavigate } from "react-router-dom";
import { TournamentCard } from "./Tournaments";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type PageId = "home" | "blog" | "merch" | "events" | "tournaments" | "about" | "dashboard";
type TournamentStatus = "upcoming" | "ongoing" | "past";
type EventStatus = "upcoming" | "ongoing" | "past";
type LanguageCode = "en" | "pt" | "es" | "fr" | "de";

interface Language { code: LanguageCode; label: string; flag: string; }
interface SocialLinks { instagram?: string; twitter?: string; youtube?: string; facebook?: string; discord?: string; tiktok?: string; }

interface TournamentEdition {
    edition: number;
    year: number;
    winner: string;
    prizePool: string;
}
interface Tournament {
    id: number;
    name: string;
    logo: string;
    banner: string;
    status: TournamentStatus;
    prizePool: string;
    playersPerTeam: number;
    game: string;
    date: string;
    registeredTeams: number;
    maxTeams: number;
    registerLink: string;
    editions: TournamentEdition[];
    description: string;
    location: string;
}

interface TEvent {
    id: number;
    name: string;
    flier: string;
    description: string;
    date: string;
    status: EventStatus;
    registerLink: string;
    location: string;
    category: string;
}

interface BlogComment {
    id: number;
    author: string;
    avatar: string;
    text: string;
    date: string;
}
interface BlogPost {
    id: number;
    title: string;
    excerpt: string;
    body: string;
    img: string;
    category: string;
    date: string;
    author: string;
    comments: BlogComment[];
}

interface MerchItem { id: number; name: string; price: number; img: string; badge: string | null; }

interface StaffMember {
    id: number;
    displayName: string;
    role: string;
    bio: string;
    passport: string;
    fullBody: string;
    country: string;
    state: string;
    social: SocialLinks;
    profileLink: string;
}

interface Review {
    id: number;
    author: string;
    avatar: string;
    rating: number;
    text: string;
    tournament: string;
    date: string;
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const css: string = ``;

// ─── STATIC DATA ──────────────────────────────────────────────────────────────

const LANGUAGES: Language[] = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "pt", label: "Português", flag: "🇧🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const ORG = {
    name: "NEXUS ESPORTS", short: "NXS",
    tagline: "Where Champions Are Made",
    description: "Nexus Esports is a premier tournament organizer founded in 2019, dedicated to delivering world-class competitive gaming events. From grassroots cups to international championships, we create unforgettable experiences for players and spectators alike.",
    logoImg: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=200&q=80",
    heroImg: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=700&q=80",
    address: "42 Arena Boulevard, Tech District, São Paulo, SP 01310-100, Brazil",
    openingHours: [
        { day: "Monday – Friday", time: "09:00 – 18:00" },
        { day: "Saturday", time: "10:00 – 16:00" },
        { day: "Sunday", time: "Closed" },
    ],
    emails: ["events@nexusesports.gg", "support@nexusesports.gg"],
    phones: ["+55 11 4002-8922", "+55 11 4002-8900"],
    social: { instagram: "@nexusesports", twitter: "@NexusEsports_GG", youtube: "Nexus Esports", discord: "discord.gg/nexus", facebook: "fb.com/nexusesports" },
    stats: { tournaments: 48, totalPlayers: 12400, totalPrize: "$2.4M", countries: 38 },
    sponsors: ["NVIDIA", "Red Bull", "HyperX", "Samsung", "Discord", "Epic Games"],
};

const TOURNAMENTS: Tournament[] = [
    {
        id: 1, name: "Nexus World Championship", logo: "🏆", status: "ongoing",
        banner: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&q=80",
        prizePool: "$100,000", playersPerTeam: 5, game: "League of Legends",
        date: "Feb 28 – Mar 10, 2026", registeredTeams: 28, maxTeams: 32,
        registerLink: "#", location: "São Paulo, Brazil",
        description: "The flagship annual championship bringing together the best teams from across the globe.",
        editions: [
            { edition: 4, year: 2025, winner: "Team Alpha", prizePool: "$80,000" },
            { edition: 3, year: 2024, winner: "Nova Esports", prizePool: "$60,000" },
            { edition: 2, year: 2023, winner: "Team Alpha", prizePool: "$40,000" },
            { edition: 1, year: 2022, winner: "Phantom GG", prizePool: "$20,000" },
        ]
    },
    {
        id: 2, name: "Spring Invitational Cup", logo: "🥇", status: "upcoming",
        banner: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=800&q=80",
        prizePool: "$25,000", playersPerTeam: 5, game: "Valorant",
        date: "Mar 15–20, 2026", registeredTeams: 12, maxTeams: 16,
        registerLink: "#", location: "Online",
        description: "Open-bracket spring invitational for rising teams looking to make their mark.",
        editions: [
            { edition: 2, year: 2025, winner: "Clutch Kings", prizePool: "$20,000" },
            { edition: 1, year: 2024, winner: "Rising Stars", prizePool: "$15,000" },
        ]
    },
    {
        id: 3, name: "Nexus Amateur League S3", logo: "⭐", status: "upcoming",
        banner: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80",
        prizePool: "$10,000", playersPerTeam: 5, game: "CS2",
        date: "Apr 1–30, 2026", registeredTeams: 6, maxTeams: 24,
        registerLink: "#", location: "Online",
        description: "A month-long amateur league for teams looking to build experience in a structured format.",
        editions: [
            { edition: 2, year: 2025, winner: "Dark Matter", prizePool: "$8,000" },
        ]
    },
    {
        id: 4, name: "Grand Prix Open", logo: "🎖️", status: "past",
        banner: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=800&q=80",
        prizePool: "$50,000", playersPerTeam: 5, game: "Dota 2",
        date: "Jan 10–15, 2026", registeredTeams: 32, maxTeams: 32,
        registerLink: "#", location: "Rio de Janeiro, Brazil",
        description: "A high-octane open tournament concluded with record viewership and attendance.",
        editions: [
            { edition: 3, year: 2026, winner: "Storm Force", prizePool: "$50,000" },
            { edition: 2, year: 2025, winner: "Iron Legion", prizePool: "$35,000" },
            { edition: 1, year: 2024, winner: "Storm Force", prizePool: "$25,000" },
        ]
    },
    {
        id: 5, name: "Nexus Cup Qualifiers", logo: "🏅", status: "past",
        banner: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80",
        prizePool: "$5,000", playersPerTeam: 5, game: "Fortnite",
        date: "Dec 5–8, 2025", registeredTeams: 48, maxTeams: 48,
        registerLink: "#", location: "Online",
        description: "Qualifier event for the main Nexus Cup, attracting hundreds of teams worldwide.",
        editions: [{ edition: 1, year: 2025, winner: "Solo Kings", prizePool: "$5,000" }]
    },
    {
        id: 6, name: "Battle Arena Invitational", logo: "⚡", status: "past",
        banner: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=800&q=80",
        prizePool: "$30,000", playersPerTeam: 3, game: "Rocket League",
        date: "Nov 20–22, 2025", registeredTeams: 24, maxTeams: 24,
        registerLink: "#", location: "Buenos Aires, Argentina",
        description: "Three-versus-three format invitational for top Rocket League squads.",
        editions: [
            { edition: 2, year: 2025, winner: "Boost Kings", prizePool: "$30,000" },
            { edition: 1, year: 2024, winner: "Aerial Aces", prizePool: "$20,000" },
        ]
    },
];

const EVENTS: TEvent[] = [
    { id: 1, name: "Nexus Summit 2026", flier: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80", description: "Annual gathering of esports professionals, analysts, players and fans for talks, panels and networking.", date: "Apr 10, 2026", status: "upcoming", registerLink: "#", location: "São Paulo Convention Center", category: "Conference" },
    { id: 2, name: "Valo Open Day Qualifier", flier: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&q=80", description: "Open registration qualifier stream for the Spring Invitational Cup bracket.", date: "Mar 5, 2026", status: "upcoming", registerLink: "#", location: "Online", category: "Qualifier" },
    { id: 3, name: "NXS Live Finals Broadcast", flier: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&q=80", description: "Live grand finals broadcast of the Nexus World Championship at the main arena stage.", date: "Mar 10, 2026", status: "ongoing", registerLink: "#", location: "São Paulo Arena", category: "Live Event" },
    { id: 4, name: "Community Game Night", flier: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=600&q=80", description: "Monthly casual community game night open to all players across all skill levels.", date: "Feb 15, 2026", status: "past", registerLink: "#", location: "Online", category: "Community" },
    { id: 5, name: "Draft & Strategy Workshop", flier: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=600&q=80", description: "Intensive coaching and drafting workshop led by professional analysts and coaches.", date: "Jan 28, 2026", status: "past", registerLink: "#", location: "Nexus HQ, São Paulo", category: "Workshop" },
];

const BLOG_POSTS: BlogPost[] = [
    {
        id: 1, title: "Inside the Nexus World Championship: What to Expect", excerpt: "Breaking down the bracket, the teams to watch, and what makes this edition the most competitive yet.",
        body: "The fifth edition of the Nexus World Championship is shaping up to be the most fiercely contested in the tournament's history. With 28 of the 32 slots already filled, the competition pool includes last year's runner-up Nova Esports, three-time champions Team Alpha, and a slew of hungry challengers from the LATAM and SEA regions.\n\nOur analysis team has been tracking form across the past six months of competitive play. The meta shift brought by the latest patch has leveled the playing field significantly, meaning upsets are more likely than ever. Watch out for Phantom GG's aggressive early-game strategies and Dark Matter's pristine team coordination.",
        img: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=700&q=80",
        category: "Tournament", date: "Feb 25, 2026", author: "Alex Rivera",
        comments: [
            { id: 1, author: "GamerX99", avatar: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=60&q=80", text: "Team Alpha always brings it. Can't wait!", date: "Feb 26, 2026" },
            { id: 2, author: "EsportsNerd", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&q=80", text: "Nova Esports has been scrimming like crazy. Dark horse pick for sure.", date: "Feb 26, 2026" },
        ]
    },
    {
        id: 2, title: "How We Built the Nexus Amateur League From Scratch", excerpt: "The story behind creating a structured competitive pathway for grassroots players.",
        body: "When we launched the Nexus Amateur League two years ago, the ambition was simple: build a bridge between casual play and professional competition. What we didn't anticipate was the overwhelming response from the community.\n\nSeason one attracted 18 teams. By season two we had 42. The format — a month-long round-robin followed by single-elimination playoffs — has proven itself as both fair and exciting for participants and viewers alike.",
        img: "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?w=700&q=80",
        category: "Behind the Scenes", date: "Feb 18, 2026", author: "Maria Santos",
        comments: [
            { id: 1, author: "ProspectPlayer", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&q=80", text: "This league changed my career path completely. Grateful for the opportunity.", date: "Feb 19, 2026" },
        ]
    },
    {
        id: 3, title: "Top 5 Moments from Grand Prix Open 2026",
        excerpt: "We count down the five plays, upsets and storylines that defined the best tournament of the year so far.",
        body: "The Grand Prix Open delivered in every way imaginable. Record attendance, razor-thin margins across multiple series, and a grand final that went to a deciding map with over 200,000 concurrent viewers.\n\nHere are the five moments we'll be talking about for years to come.",
        img: "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=700&q=80",
        category: "Highlights", date: "Jan 20, 2026", author: "Carlos Lima",
        comments: []
    },
    {
        id: 4, title: "Announcing the Nexus Summit 2026 Speaker Lineup",
        excerpt: "Meet the industry leaders, professional players and innovators taking the stage at this year's Summit.",
        body: "We are thrilled to reveal the lineup for Nexus Summit 2026, our annual gathering of the brightest minds in competitive gaming. This year features keynotes from top team managers, exclusive panels with active professional players, and workshops on coaching methodology, content creation, and the business of esports.",
        img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=700&q=80",
        category: "Announcement", date: "Jan 10, 2026", author: "Alex Rivera",
        comments: [
            { id: 1, author: "ContentQueen", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&q=80", text: "Already bought my ticket. The speaker list is incredible!", date: "Jan 11, 2026" },
        ]
    },
    {
        id: 5, title: "Player Welfare: How Nexus Protects Competitors",
        excerpt: "An inside look at the mental health initiatives and player support structures we've built into every event.",
        body: "The competitive gaming landscape has evolved dramatically in terms of player welfare awareness. At Nexus, we've made it a priority to embed mental health support, fair play policies, and structured rest periods into every event we run.",
        img: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=700&q=80",
        category: "Community", date: "Dec 20, 2025", author: "Maria Santos",
        comments: []
    },
    {
        id: 6, title: "The Rise of LATAM Esports: A Nexus Perspective",
        excerpt: "Exploring the rapid growth of South American competitive gaming and what it means for the global scene.",
        body: "Five years ago, LATAM teams were considered mid-tier on the global competitive stage. Today, they are legitimate title contenders at every major international event. Nexus has had a front-row seat to this transformation, having run over 20 regional events in Brazil, Argentina and Colombia.",
        img: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=700&q=80",
        category: "Industry", date: "Dec 5, 2025", author: "Carlos Lima",
        comments: []
    },
];

const MERCH: MerchItem[] = [
    { id: 1, name: "NXS Championship Hoodie", price: 64.99, img: "https://images.unsplash.com/photo-1556821840-3a63f15732ce?w=400&q=80", badge: "New" },
    { id: 2, name: "Official Event Jersey", price: 54.99, img: "https://images.unsplash.com/photo-1620794108219-aedbde4e6fb0?w=400&q=80", badge: null },
    { id: 3, name: "NXS Snapback Cap", price: 29.99, img: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=400&q=80", badge: "Hot" },
    { id: 4, name: "Champion Trophy Replica", price: 149.99, img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=400&q=80", badge: "Limited" },
    { id: 5, name: "Gaming Mouse Pad XL", price: 24.99, img: "https://images.unsplash.com/photo-1599481238640-4c1288750d7a?w=400&q=80", badge: null },
    { id: 6, name: "NXS World Champ Tee", price: 34.99, img: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=400&q=80", badge: null },
];

const STAFF: StaffMember[] = [
    { id: 1, displayName: "Marcus Wei", role: "CEO & Founder", bio: "Visionary behind Nexus Esports with 10+ years in competitive gaming organization.", passport: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1571945192063-40f7fd5f7a49?w=500&q=80", country: "🇧🇷 Brazil", state: "São Paulo", social: { instagram: "@marcus_wei", twitter: "@marcuswei_nxs" }, profileLink: "#" },
    { id: 2, displayName: "Sofia Ramos", role: "Head of Operations", bio: "Oversees all tournament logistics, staffing and venue partnerships across the portfolio.", passport: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=500&q=80", country: "🇧🇷 Brazil", state: "Rio de Janeiro", social: { instagram: "@sofia_nxs", twitter: "@sofiaramos_nxs" }, profileLink: "#" },
    { id: 3, displayName: "Pedro Alvarez", role: "Tournament Director", bio: "Designs bracket formats and competitive rulesets. Former semi-pro player and analyst.", passport: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=500&q=80", country: "🇦🇷 Argentina", state: "Buenos Aires", social: { twitter: "@pedro_td", discord: "PedroTD#1234" }, profileLink: "#" },
    { id: 4, displayName: "Aisha Okonkwo", role: "Marketing Manager", bio: "Leads brand strategy, social media and partnerships across all Nexus properties.", passport: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80", fullBody: "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=500&q=80", country: "🇳🇬 Nigeria", state: "Lagos", social: { instagram: "@aisha_nxs", twitter: "@aishaokonkwo" }, profileLink: "#" },
];

const REVIEWS: Review[] = [
    { id: 1, author: "ProGamer_BR", avatar: "https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=60&q=80", rating: 5, text: "The production quality at the Grand Prix Open was absolutely world class. Every match felt like a main stage event. Will be back every year.", tournament: "Grand Prix Open", date: "Jan 16, 2026" },
    { id: 2, author: "TeamCaptain99", avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&q=80", rating: 5, text: "Registration was seamless, rules were clear and the referee team was professional throughout. Nexus genuinely cares about fair competition.", tournament: "Nexus Amateur League", date: "Nov 30, 2025" },
    { id: 3, author: "StreamerKing", avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&q=80", rating: 4, text: "Amazing broadcast quality and production value. The observer work during finals was cinematic. Small delay issues in group stage though.", tournament: "Nexus World Championship", date: "Feb 26, 2026" },
    { id: 4, author: "FPS_GOAT", avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&q=80", rating: 5, text: "Spring Invitational is my favorite tournament of the year. Great prize pool, tight competition and the Nexus staff are super responsive.", tournament: "Spring Invitational Cup", date: "Sep 8, 2025" },
    { id: 5, author: "AnalystMaria", avatar: "https://images.unsplash.com/photo-1545996124-0501ebae84d0?w=60&q=80", rating: 5, text: "The Summit conference had incredible speakers and real actionable content. Best esports conference I've attended in years.", tournament: "Nexus Summit 2025", date: "Apr 15, 2025" },
    { id: 6, author: "RocketQueen", avatar: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=60&q=80", rating: 4, text: "Battle Arena Invitational was fire. Wish the format allowed more teams but the double-elimination playoffs were perfect.", tournament: "Battle Arena Invitational", date: "Nov 23, 2025" },
];

const PAGE_IDS: PageId[] = ["home", "tournaments", "events", "blog", "merch", "about"];
const PAGE_LABELS: Record<PageId, string> = {
    home: "Home", tournaments: "Tournaments", events: "Events", blog: "Blog",
    merch: "Merchandise", about: "About", dashboard: "Dashboard",
};
const SPONSORS: string[] = ORG.sponsors;

interface HomeProps {
    basePath: string;
    website: IHostWebsite;
}

function Home(props: HomeProps) {

    const navigate = useNavigate();
    const { basePath, website } = props;
    const nameSplit = website.host.name.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const omnehostService = OmneHostService.getInstance();
    const [events, setEvents] = React.useState<IWebsiteEvent[]>();
    const [articles, setArticles] = React.useState<IHostBlogArticle[]>();
    const [tournaments, setTournaments] = React.useState<ITournament[]>();

    React.useEffect(fetchInformation, []);

    return (
        <>
            {/* HERO */}
            <section className={styles["hero"]} style={{ marginBottom: "5rem" }}>
                <div className={styles["hero-bg"]} />
                <div className={styles["hero-grid-bg"]} />
                <div className={styles["hero-content"]}>
                    <div>
                        <div className={styles["hero-tag"]}>🎮 Official Tournament Organizer</div>
                        <h1 className={styles["hero-name"]}>{nameFirstWord}{(nameSplit?.length > 0) && (<><br /><em>{nameRemain.join(" ")}</em></>)}</h1>
                        {/* <p className={styles["hero-tagline"]}>{ORG.tagline}</p> */}
                        <p className={styles["hero-bio"]}>{website.host.short_info}</p>
                        <div className={styles["hero-stats"]}>
                            <div>
                                <span className={styles["h-stat-val"]}>{website.host.meta.tournaments_count}</span>
                                <span className={styles["h-stat-lbl"]}>Tournaments</span>
                            </div>
                            <div>
                                <span className={styles["h-stat-val"]}>{website.host.meta.countries.length}</span>
                                <span className={styles["h-stat-lbl"]}>Countries</span>
                            </div>
                            {/* {([["48", "Tournaments"], ["12.4K", "Players"], ["$2.4M", "Total Prize"], ["38", "Countries"]] as [string, string][]).map(([v, l]) => (
                                
                            ))} */}
                        </div>
                        <div className={styles["hero-cta"]}>
                            {(website.website_pages?.TOURNAMENTS?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/tournaments`} className={styles["btn-primary"]} style={{ textDecoration: "none" }}>View Tournaments</ReactRouterDom.NavLink>)}
                            {(website.website_pages?.TOURNAMENTS?.visible) && (<ReactRouterDom.NavLink end to={`/${basePath}/about`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>About Us</ReactRouterDom.NavLink>)}
                        </div>
                    </div>
                    <div className={styles["hero-img-wrap"]}>
                        <div className={styles["hero-img-glow"]} />
                        <img
                            className={styles["hero-org-img"]}
                            src={website.host.logo_url}
                            alt={website.host.name}
                            style={{ width: "100%", maxWidth: "440px", height: "560px", objectFit: "cover", borderRadius: "24px", border: "1px solid rgba(201,168,76,0.15)", boxShadow: "0 40px 80px rgba(0,0,0,0.6)", position: "relative", zIndex: 1, display: "block" }}
                        />
                        <div className={styles["hero-img-badge"]}>
                            <span className={styles["hero-img-badge-val"]}>{website.host.meta.tournaments_count}</span>
                            <span className={styles["hero-img-badge-lbl"]}>Tournaments</span>
                        </div>
                        <div className={styles["hero-img-tag"]}><strong>Est. {new Date(website.host.created_at).getFullYear()}</strong><span>{i18nManager.queryCountryCurrency(website.host.country_code, "country_code", "country_name")}</span></div>
                    </div>
                </div>
                <div className={styles["hero-emblem"]}>{nameSplit.length > 1 ? nameSplit.map((s) => s[0]).join("") : website.host.name}</div>
            </section>

            {/* SPONSORS */}
            {(website.home_settings?.show_sponsors && website.sponsors?.length) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Our <em>Sponsors</em></h2>
                <p className={styles["sec-sub"]}>Partners fuelling competitive excellence</p>
                <div className={styles["sponsors-list"]}>
                    {website.sponsors.map((s) => (
                        <div key={s} className={styles["sponsor-chip"]}>
                            <img alt={s} src={s} />
                        </div>
                    ))}
                </div>
            </div>)}

            {(!!tournaments?.length) && (
                <div className={styles["section"]} style={{ paddingTop: 0 }}>
                    <div className={styles["sec-line"]} />
                    <div className={styles["sec-header"]}>
                        <div>
                            <h2 className={styles["sec-title"]}>Live <em>Tournaments</em></h2>
                            <p className={styles["sec-sub"]} style={{  }}>Currently running</p>
                        </div>
                        <button className={styles["btn-ghost"]} onClick={() => navigate("tournaments")}>All Tournaments →</button>
                    </div>
                    <div style={{ marginTop: "2rem" }} className={styles["tournament-grid"]}>
                        {tournaments.map(t => <TournamentCard key={t.external_id} tournament={t} onSelect={(t) => {
                                navigate({
                                    pathname: `/${basePath}/tournaments`,
                                    search: "tournament=" + t.external_id
                                });
                        }} />)}
                    </div>
                </div>
            )}

            {/* RECENT BLOG */}
            <div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Recent <em>Blog Posts</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>News and insights</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/blog`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>All Posts →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={styles["blog-grid"]}>
                    {articles?.map((p) => {
                        return buildArticleView({
                            p,
                            onSelect: (p) => {
                                navigate({
                                    pathname: `/${basePath}/blog`,
                                    search: "article=" + p.external_id
                                });
                            },
                        });
                    })}
                </div>
            </div>

            {/* ABOUT STRIP */}
            <div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>About <em>{website.host.name}</em></h2>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", alignItems: "center" }}>
                    <div>
                        <p style={{ color: "var(--muted)", lineHeight: 1.8, fontSize: "0.95rem", marginBottom: "2rem" }}>{website.about_settings.info}</p>
                        <ReactRouterDom.NavLink end to={`/${basePath}/about#managers`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>Meet the Team →</ReactRouterDom.NavLink>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                        {/* {([["👥", "12,400+", "Players Competed"], ["💰", "$2.4M", "Prize Distributed"]] as [string, string, string][]).map(([icon, v, l]) => (
                            <div key={l} style={{ background: "var(--dark3)", borderRadius: "14px", padding: "1.25rem", border: "1px solid rgba(255,255,255,0.05)" }}>
                                <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>{icon}</div>
                                <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.8rem", color: "var(--gold)", letterSpacing: "1px", lineHeight: 1 }}>{v}</div>
                                <div style={{ color: "var(--muted)", fontSize: "0.72rem", marginTop: "0.3rem", letterSpacing: "0.5px" }}>{l}</div>
                            </div>
                        ))} */}
                        <div style={{ background: "var(--dark3)", borderRadius: "14px", padding: "1.25rem", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>🏆</div>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.8rem", color: "var(--gold)", letterSpacing: "1px", lineHeight: 1 }}>{website.host.meta.tournaments_count}</div>
                            <div style={{ color: "var(--muted)", fontSize: "0.72rem", marginTop: "0.3rem", letterSpacing: "0.5px" }}>Tournaments Hosted</div>
                        </div>
                        <div style={{ background: "var(--dark3)", borderRadius: "14px", padding: "1.25rem", border: "1px solid rgba(255,255,255,0.05)" }}>
                            <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>🌍</div>
                            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: "1.8rem", color: "var(--gold)", letterSpacing: "1px", lineHeight: 1 }}>{website.host.meta.countries.length}</div>
                            <div style={{ color: "var(--muted)", fontSize: "0.72rem", marginTop: "0.3rem", letterSpacing: "0.5px" }}>Countries Reached</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* UPCOMING EVENTS */}
            <div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <div className={styles["sec-header"]}>
                    <div>
                        <h2 className={styles["sec-title"]}>Upcoming <em>Events</em></h2>
                        <p className={styles["sec-sub"]} style={{ marginBottom: 0 }}>What's on the calendar</p>
                    </div>
                    <ReactRouterDom.NavLink end to={`/${basePath}/events`} className={styles["btn-ghost"]} style={{ textDecoration: "none" }}>All Events →</ReactRouterDom.NavLink>
                </div>
                <div style={{ marginTop: "2rem" }} className={styles["events-grid"]}>
                    {events?.map?.(buildEventView)}
                </div>
            </div>

            {/* SOCIAL */}
            {(Object.keys(website.social_accounts ?? {}).length > 0) && (<div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Social <em>Networks</em></h2>
                <p className={styles["sec-sub"]}>Follow Nexus Esports across all platforms</p>
                <div className={styles["social-grid"]}>
                    {Object.entries(website.social_accounts ?? {}).map(([social, username]) => (<ReactRouterDom.NavLink target="_blank" key={social} to={Utils.getSocialLink(social, username)} className={Classname.build(styles["social-card"], styles["unlinked"])}>
                        <i className={Classname.build(styles["social-icon"], Utils.getSocialIcon(social))} />
                        <div className={styles["social-name"]}>{social}</div>
                        <div className={styles["social-handle"]}>{username}</div>
                    </ReactRouterDom.NavLink>))}
                </div>
            </div>)}

            {/* REVIEWS */}
            <div className={styles["section"]} style={{ paddingTop: 0 }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]}>Participant <em>Reviews</em></h2>
                <p className={styles["sec-sub"]}>What players and teams say about our tournaments</p>
                <div className={styles["reviews-grid"]}>
                    {REVIEWS.map(r => (
                        <div key={r.id} className={styles["review-card"]}>
                            <div className={styles["review-header"]}>
                                <img className={styles["review-avatar"]} src={r.avatar} alt={r.author} loading="lazy" />
                                <div>
                                    <div className={styles["review-author"]}>{r.author}</div>
                                    <div className={styles["review-tournament"]}>{r.tournament}</div>
                                </div>
                                <div className={styles["review-stars"]}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                            </div>
                            <div className={styles["review-text"]}>{r.text}</div>
                            <div className={styles["review-date"]}>{r.date}</div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );

    function fetchInformation() {
        omnehostService.queryHostEvents(props.website.host.external_id, {
            size: 3,
            sort: "start_date,DESC",
        }).then(({ content }: SanitizedResponse<IWebsiteEvent>) => {
            setEvents(content);
        }).catch(BaseService.reportError);
        omnehostService.queryHostBlogArticles(website.host.external_id, {
            size: 3,
            sort: "updated_at,DESC",
        }).then(({ content }: SanitizedResponse<IHostBlogArticle>) => {
            setArticles(content);
        }).catch(BaseService.reportError);
        omnehostService.queryHostTournaments(website.host.external_id, {
            status: "UPCOMING",
            sort: "public_id,DESC",
        }).then(({ content }: SanitizedResponse<ITournament>) => {
            setTournaments(content);
        }).catch(BaseService.reportError);
    }

}

export default Home;